import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { SvgIconProps } from '@mui/material/SvgIcon';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Battery6BarIcon from '@mui/icons-material/Battery6Bar';
import Battery4BarIcon from '@mui/icons-material/Battery4Bar';
import Battery2BarIcon from '@mui/icons-material/Battery2Bar';
import InfoIcon from '@mui/icons-material/Info';
import BatteryUnknownIcon from '@mui/icons-material/BatteryUnknown';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useDictionary from '@ui/hooks/useDictionary';
import getCharge from '@utils/getCharge';
import encodeCol from '@utils/encodeCol';
import encodeSortModel from '@utils/encodeSortModel';
import { Menu } from '@ui/components/Menu/menu';
import { IMenuItem } from '@ui/components/Menu/IMenuItem';
import { IProps as IMainProps } from './Device-item.component';

interface IProps
    extends Pick<
        IMainProps,
        | 'lang'
        | 'columnVisibilityModel'
        | 'dateFilter'
        | 'fromDateFilter'
        | 'zoom'
        | 'toDateFilter'
        | 'sortModel'
        | 'date'
        | 'batteryVoltage'
        | 'id'
        | 'coords'
        | 'settings'
        | 'onDeviceDeleteOpen'
        | 'onDeviceDescriptionOpen'
        | 'onDeviceTelemetryOpen'
        | 'onDeviceBtnHoverStart'
        | 'onDeviceBtnHoverStop'
        | 'onDeviceSettingsOpen'
        | 'onDeviceInfoOpen'
    > {}

const Component: React.FC<IProps> = ({
    date,
    batteryVoltage,
    lang,
    id,
    coords,
    columnVisibilityModel,
    dateFilter,
    fromDateFilter,
    toDateFilter,
    zoom,
    sortModel,
    settings,
    onDeviceTelemetryOpen,
    onDeviceBtnHoverStart,
    onDeviceBtnHoverStop,
    onDeviceInfoOpen,
    onDeviceSettingsOpen,
    onDeviceDeleteOpen,
    onDeviceDescriptionOpen,
}: IProps) => {
    const getWord = useDictionary();
    const isSettings = true;//settings && settings.status && !settings.status.error;
    let timeMessage: string;
    let timeColor: SvgIconProps['color'];
    if (date) {
        const timeout = Math.ceil(
            (Date.now() - new Date(date).getTime()) / 1000 / 3600,
        );
        if (timeout < 25) {
            timeMessage = getWord('deviceContactedToday');
            timeColor = 'success';
        } else if (timeout < 72) {
            timeMessage = getWord('deviceNotContactedFor24hours');
            timeColor = 'warning';
        } else {
            timeMessage = getWord('deviceNotContactedFor3days');
            timeColor = 'error';
        }
    } else {
        timeMessage = getWord('deviceNotContactedYet');
        timeColor = 'disabled';
    }
    let batteryMessage: string;
    let batteryColor: SvgIconProps['color'];
    let BatteryIcon: typeof Battery6BarIcon;
    if (batteryVoltage) {
        const charge = getCharge(batteryVoltage);
        batteryMessage = `${getWord('batteryVoltage')} ${batteryVoltage.toFixed(
            2,
        )} ${getWord('V')}`;
        if (charge > 50) {
            batteryColor = 'success';
            BatteryIcon = Battery6BarIcon;
        } else if (charge >= 25) {
            batteryColor = 'warning';
            BatteryIcon = Battery4BarIcon;
        } else {
            batteryColor = 'error';
            BatteryIcon = Battery2BarIcon;
        }
    } else {
        batteryMessage = getWord('noDataAboutBattery');
        batteryColor = 'disabled';
        BatteryIcon = BatteryUnknownIcon;
    }
    const search = new URLSearchParams();
    const [cvm, cvmKeys] = encodeCol(columnVisibilityModel);
    let df: string;
    switch (dateFilter) {
        case 'last24Hours':
            df = '';
            break;
        case 'last3Days':
            df = '1';
            break;
        case 'lastWeek':
            df = '2';
            break;
        case 'customDate':
            df = '3';
            break;
        default:
            df = '';
            break;
    }
    const dff = fromDateFilter.getTime();
    const dft = toDateFilter.getTime();
    const sm = encodeSortModel(sortModel);
    search.set('lang', lang);
    search.set('sd', id);
    search.set('tmv', 'true');
    search.set('cvm', cvm);
    search.set('df', df);
    search.set('dff', `${dff}`);
    search.set('dft', `${dft}`);
    search.set('z', `${zoom}`);
    search.set('sm', sm);
    if (coords) search.set('p', String(coords));

    let menuItems: IMenuItem[] = [
        {
            text: 'infoAboutDevice',
            id: id,
            icon: <InfoIcon className="groups__item-menu-ico_item" />,
            onClick: onDeviceInfoOpen,
        },
        {
            text: 'openTelemetry',
            id: id,
            icon: <HistoryIcon className="groups__item-menu-ico_item" />,
            onClick: onDeviceTelemetryOpen,
        },
        {
            text: 'editDeviceDescription',
            id: id,
            icon: <EditIcon className="groups__item-menu-ico_item" />,
            onClick: onDeviceDescriptionOpen,
        },
        {
            text: 'deviceDelete',
            id: id,
            icon: <DeleteIcon className="groups__item-menu-ico_item" />,
            onClick: onDeviceDeleteOpen,
        },
    ];
    if (isSettings) {
        menuItems = [
            ...menuItems.slice(0, 2),
            {
                text: 'deviceSettings',
                id: id,
                icon: <SettingsIcon className="groups__item-menu-ico_item" />,
                onClick: onDeviceSettingsOpen,
            },
            ...menuItems.slice(2),
        ];
    }
    const menuList = menuItems.map((item) => ({
        ...item,
        text: getWord(item.text),
    }));

    return (
        <>
            {/* <Tooltip
                classes={{
                    popper: 'groups__item-menu-tooltip-popper',
                    tooltip: 'groups__item-menu-tooltip',
                }}
                title={batteryMessage}
                placement="top"
                onTouchStartCapture={onDeviceBtnHoverStart}
                onTouchEndCapture={onDeviceBtnHoverStop}
                onMouseOver={onDeviceBtnHoverStart}
                onMouseOut={onDeviceBtnHoverStop}
                onMouseEnter={onDeviceBtnHoverStart}
                onFocus={onDeviceBtnHoverStart}
                onMouseLeave={onDeviceBtnHoverStop}
                onBlur={onDeviceBtnHoverStop}
            > */}
                <BatteryIcon
                    className="groups__item-menu-ico_info"
                    color={batteryColor}
                />
            {/* </Tooltip>
            <Tooltip
                classes={{
                    popper: 'groups__item-menu-tooltip-popper',
                    tooltip: 'groups__item-menu-tooltip',
                }}
                title={timeMessage}
                placement="top"
                onTouchStartCapture={onDeviceBtnHoverStart}
                onTouchEndCapture={onDeviceBtnHoverStop}
                onMouseOver={onDeviceBtnHoverStart}
                onMouseOut={onDeviceBtnHoverStop}
                onMouseEnter={onDeviceBtnHoverStart}
                onFocus={onDeviceBtnHoverStart}
                onMouseLeave={onDeviceBtnHoverStop}
                onBlur={onDeviceBtnHoverStop}
            > */}
                <AccessTimeIcon
                    className="groups__item-menu-ico_info"
                    color={timeColor}
                />
            {/* </Tooltip> */}
            <Menu
                label="additionally"
                tooltip
                button={<MoreVertIcon className="groups__item-menu-ico" />}
                items={menuList}
                onHoverStart={onDeviceBtnHoverStart}
                onHoverStop={onDeviceBtnHoverStop}
            />
        </>
    );
};

const DeviceMenuComponent = React.memo(Component);

export default DeviceMenuComponent;
